// Mock data for BrixBot AI chatbot business website

export const mockData = {
  // Company information
  company: {
    name: "BrixBot",
    tagline: "Intelligent Customer Service, Powered by Your Data",
    description: "Transform your customer service with AI chatbots trained specifically on your business data. Provide instant, accurate responses 24/7."
  },

  // Features data
  features: [
    {
      id: 1,
      title: "Data-Driven Intelligence",
      description: "Our AI chatbots are trained on your specific business data, ensuring accurate and relevant responses to customer queries.",
      icon: "Brain",
      benefits: ["Learns from your knowledge base", "Understands your products/services", "Maintains brand voice"]
    },
    {
      id: 2,
      title: "24/7 Customer Support",
      description: "Never miss a customer inquiry. Your AI chatbot works around the clock to provide instant support.",
      icon: "Clock",
      benefits: ["Round-the-clock availability", "Instant response times", "Reduces support workload"]
    },
    {
      id: 3,
      title: "Easy Integration",
      description: "Simple setup process that gets your chatbot running quickly on your website with minimal technical requirements.",
      icon: "Zap",
      benefits: ["Quick website integration", "Minimal setup time", "User-friendly implementation"]
    },
    {
      id: 4,
      title: "Advanced Analytics",
      description: "Get insights into customer behavior, common questions, and chatbot performance.",
      icon: "BarChart3",
      benefits: ["Detailed conversation analytics", "Customer satisfaction metrics", "Performance optimization"]
    },
    {
      id: 5,
      title: "Customizable Design",
      description: "Match your brand with fully customizable chatbot interface and personality.",
      icon: "Palette",
      benefits: ["Brand-consistent design", "Custom conversation flows", "Personalized responses"]
    },
    {
      id: 6,
      title: "Multilingual Support",
      description: "Serve customers in their preferred language with built-in translation capabilities.",
      icon: "Globe",
      benefits: ["Multiple language support", "Auto-translation", "Cultural context awareness"]
    }
  ],

  // Pricing plans
  pricing: {
    setup: {
      total: 600,
      downPayment: 200,
      remaining: 400,
      currency: "$"
    },
    monthly: {
      price: 25,
      currency: "$",
      billing: "month"
    },
    yearly: {
      price: 240,
      originalPrice: 300,
      discount: 20,
      currency: "$",
      billing: "year"
    }
  },

  // Testimonials
  testimonials: [
    {
      id: 1,
      name: "Sarah Johnson",
      company: "TechStart Solutions",
      role: "CEO",
      content: "BrixBot revolutionized our customer service. Response times dropped from hours to seconds, and customer satisfaction increased by 40%.",
      rating: 5,
      avatar: "https://images.unsplash.com/photo-1494790108755-2616b9951a8c?w=150&h=150&fit=crop&crop=face"
    },
    {
      id: 2,
      name: "Michael Chen",
      company: "RetailMax",
      role: "Operations Manager",
      content: "The AI understands our products perfectly. It's like having our best sales person available 24/7. Setup was incredibly smooth.",
      rating: 5,
      avatar: "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=150&h=150&fit=crop&crop=face"
    },
    {
      id: 3,
      name: "Emma Rodriguez",
      company: "HealthCare Plus",
      role: "Customer Success Lead",
      content: "Our patients love the instant responses. The chatbot handles 80% of routine inquiries, letting our team focus on complex cases.",
      rating: 5,
      avatar: "https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=150&h=150&fit=crop&crop=face"
    }
  ],

  // FAQ data
  faq: [
    {
      id: 1,
      question: "How long does it take to set up a chatbot?",
      answer: "Initial setup takes 3-5 business days. We'll work with you to train the AI on your data and customize it to your needs."
    },
    {
      id: 2,
      question: "What data do you need to train the chatbot?",
      answer: "We work with your existing knowledge base, FAQ documents, product catalogs, and support tickets to train the AI effectively."
    },
    {
      id: 3,
      question: "Can I customize the chatbot's personality?",
      answer: "Absolutely! We customize the chatbot's tone, style, and personality to match your brand voice and customer expectations."
    },
    {
      id: 4,
      question: "What happens if the chatbot can't answer a question?",
      answer: "The chatbot will gracefully escalate to your human support team and learn from the interaction for future improvements."
    }
  ],

  // Navigation links
  navigation: [
    { name: "Home", href: "/" },
    { name: "Features", href: "/features" },
    { name: "Pricing", href: "/pricing" },
    { name: "Demo", href: "/demo" },
    { name: "About", href: "/about" },
    { name: "Contact", href: "/contact" }
  ],

  // Stats for homepage
  stats: [
    { label: "Always Active Chatbot", value: "" },
    { label: "All Your Messages Handled", value: "" },
    { label: "Guaranteed Customer Satisfaction", value: "" },
    { label: "Ultra Fast Response Time", value: "" }
  ],

  // Demo conversation
  demoConversation: [
    {
      id: 1,
      type: "user",
      message: "Hi, I'm interested in your premium plan. What's included?",
      timestamp: "10:30 AM"
    },
    {
      id: 2,
      type: "bot",
      message: "Hello! I'd be happy to help you with our premium plan. It includes unlimited projects, priority support, advanced analytics, and custom integrations. Would you like me to walk you through the specific features?",
      timestamp: "10:30 AM"
    },
    {
      id: 3,
      type: "user",
      message: "Yes, tell me more about the analytics.",
      timestamp: "10:31 AM"
    },
    {
      id: 4,
      type: "bot",
      message: "Our advanced analytics provide detailed insights into user behavior, conversion rates, popular features, and performance metrics. You'll get real-time dashboards, custom reports, and data export capabilities. This helps you make data-driven decisions to improve your business.",
      timestamp: "10:31 AM"
    }
  ],

  // User authentication mock
  mockUser: {
    id: 1,
    email: "user@example.com",
    name: "John Doe",
    company: "Demo Company",
    plan: "starter",
    chatbotStatus: "active",
    messagesThisMonth: 1247,
    joinDate: "2024-01-15"
  }
};

// Mock API functions
export const mockAPI = {
  // Authentication
  login: async (credentials) => {
    await new Promise(resolve => setTimeout(resolve, 1000));
    if (credentials.email && credentials.password) {
      localStorage.setItem('isAuthenticated', 'true');
      localStorage.setItem('user', JSON.stringify(mockData.mockUser));
      return { success: true, user: mockData.mockUser };
    }
    throw new Error('Invalid credentials');
  },

  register: async (userData) => {
    await new Promise(resolve => setTimeout(resolve, 1000));
    if (userData.email && userData.password && userData.name) {
      const newUser = { ...mockData.mockUser, ...userData, id: Date.now() };
      localStorage.setItem('isAuthenticated', 'true');
      localStorage.setItem('user', JSON.stringify(newUser));
      return { success: true, user: newUser };
    }
    throw new Error('Invalid registration data');
  },

  logout: () => {
    localStorage.removeItem('isAuthenticated');
    localStorage.removeItem('user');
  },

  // Contact form
  submitContact: async (formData) => {
    await new Promise(resolve => setTimeout(resolve, 1000));
    console.log('Contact form submitted:', formData);
    return { success: true, message: 'Thank you for your message. We\'ll get back to you soon!' };
  },

  // Demo request
  requestDemo: async (demoData) => {
    await new Promise(resolve => setTimeout(resolve, 1000));
    console.log('Demo requested:', demoData);
    return { success: true, message: 'Demo scheduled! We\'ll contact you shortly.' };
  },

  // Payment simulation
  processPayment: async (paymentData) => {
    await new Promise(resolve => setTimeout(resolve, 2000));
    console.log('Payment processed:', paymentData);
    return { success: true, message: 'Payment successful! Your chatbot setup will begin shortly.' };
  }
};

export default mockData;